// ADR-0054 §1 / V101-5-2 (D-CI-18): reviewed remediation hints per check key.
// The remediation coach grounds every draft on one of these; with no hint it
// refuses. Steps say what to change in the customer's own console or IaC;
// GAC Pro never applies them (ADR-0017). Provenance is required: a hint with
// `reviewer: null` is drafted content awaiting owner review, and drafts built
// on it are labelled "Guidance pending review".
//
// G-1i: per-kind variants may also live next to the kind
// (connectors/hints/<kind>.ts via the manifest's `outputs.hints`); they are
// merged into `byKind` below. `baseFor` names the kinds a single-vendor base
// body was written for; any other emitter needs its own variant (conformance
// test in remediation-hints.test.ts).
import { CONNECTOR_MANIFESTS } from '../connectors/manifests.js';
// Every hint below was reviewed by the owner (s-nielsen) on 2026-10-06 on the v10.1
// sign-off page (D-CI-52; 43 of 43 approved, no changes). A new or changed hint goes
// back to DRAFT until it is reviewed again. Steps match each collector's pass rule
// (packages/cloudflare/src/adapters/*).
const tf = (...lines) => lines.join('\n');
export const DRAFT = (source) => ({ source, reviewer: null, reviewed_at: null });
const REVIEWED = (source) => ({ source, reviewer: 's-nielsen', reviewed_at: '2026-10-06' });
const AWS_CLOUDTRAIL_TF = tf('resource "aws_cloudtrail" "org" {', '  name                          = "org-trail"', '  s3_bucket_name                = var.trail_bucket_name', '  is_multi_region_trail         = true', '  include_global_service_events = true', '  enable_log_file_validation    = true', '  enable_logging                = true', '}');
const AWS_CONFIG_RECORDER_TF = tf('resource "aws_config_configuration_recorder" "this" {', '  name     = "default"', '  role_arn = var.config_role_arn', '  recording_group {', '    all_supported                 = true', '    include_global_resource_types = true', '  }', '}', '', 'resource "aws_config_delivery_channel" "this" {', '  name           = "default"', '  s3_bucket_name = var.config_bucket_name', '  depends_on     = [aws_config_configuration_recorder.this]', '}', '', 'resource "aws_config_configuration_recorder_status" "this" {', '  name       = aws_config_configuration_recorder.this.name', '  is_enabled = true', '  depends_on = [aws_config_delivery_channel.this]', '}');
const AWS_SECURITYHUB_TF = tf('resource "aws_securityhub_account" "this" {}', '', 'resource "aws_securityhub_standards_subscription" "fsbp" {', '  depends_on    = [aws_securityhub_account.this]', '  standards_arn = "arn:aws:securityhub:${var.region}::standards/aws-foundational-security-best-practices/v/1.0.0"', '}');
const AWS_INSPECTOR_TF = tf('resource "aws_inspector2_enabler" "this" {', '  account_ids    = [var.account_id]', '  resource_types = ["EC2", "ECR"]', '}');
const AWS_EBS_DEFAULT_TF = tf('resource "aws_ebs_encryption_by_default" "this" {', '  enabled = true', '}');
const AWS_BACKUP_TF = tf('resource "aws_backup_vault" "this" {', '  name = "default-vault"', '}', '', 'resource "aws_backup_plan" "daily" {', '  name = "daily"', '  rule {', '    rule_name         = "daily-35d"', '    target_vault_name = aws_backup_vault.this.name', '    schedule          = "cron(0 5 * * ? *)"', '    lifecycle {', '      delete_after = 35', '    }', '  }', '}');
const AWS_BEDROCK_LOGGING_TF = tf('resource "aws_bedrock_model_invocation_logging_configuration" "this" {', '  logging_config {', '    text_data_delivery_enabled      = true', '    image_data_delivery_enabled     = false', '    embedding_data_delivery_enabled = false', '    video_data_delivery_enabled     = false', '    cloudwatch_config {', '      log_group_name = var.bedrock_log_group_name', '      role_arn       = var.bedrock_logging_role_arn', '    }', '  }', '}');
const AWS_BEDROCK_GUARDRAIL_TF = tf('resource "aws_bedrock_guardrail" "default" {', '  name                      = "default"', '  blocked_input_messaging   = var.blocked_input_message', '  blocked_outputs_messaging = var.blocked_output_message', '  content_policy_config {', '    filters_config {', '      type            = "HATE"', '      input_strength  = "MEDIUM"', '      output_strength = "MEDIUM"', '    }', '    filters_config {', '      type            = "PROMPT_ATTACK"', '      input_strength  = "HIGH"', '      output_strength = "NONE"', '    }', '  }', '}');
const gcpOrgPolicyTf = (constraint) => tf(`resource "google_org_policy_policy" "${constraint.replace(/\./g, '_')}" {`, `  name   = "organizations/\${var.org_id}/policies/${constraint}"`, '  parent = "organizations/${var.org_id}"', '  spec {', '    rules {', '      enforce = "TRUE"', '    }', '  }', '}');
const GITHUB_BRANCH_PROTECTION_TF = tf('resource "github_branch_protection" "default" {', '  repository_id  = var.repository_node_id', '  pattern        = var.default_branch', '  enforce_admins = true', '  required_pull_request_reviews {', '    required_approving_review_count = 1', '    dismiss_stale_reviews           = true', '  }', '}');
const GITHUB_SECRET_SCANNING_TF = tf('# Import the existing repository into state before applying.', 'resource "github_repository" "this" {', '  name = var.repository_name', '  security_and_analysis {', '    secret_scanning {', '      status = "enabled"', '    }', '    secret_scanning_push_protection {', '      status = "enabled"', '    }', '  }', '}');
const BASE_HINTS = [
    // ── Identity ──────────────────────────────────────────────────────────
    {
        check_key: 'idp_mfa_enforced',
        steps: [
            'Require a second factor for every interactive sign-in, in a policy that targets all users and all apps.',
            'Remove or tighten any rule that still allows password-only access.',
            'Exclude only documented break-glass accounts, then re-run the connector.',
        ],
        doc_url: 'https://learn.microsoft.com/en-us/entra/identity/conditional-access/policy-all-users-mfa-strength',
        byKind: {
            okta_posture: {
                steps: [
                    'In the Okta Admin Console go to Security > Global Session Policy.',
                    'In every rule whose access is Allowed, set "Multifactor authentication (MFA) is" to Required.',
                    'Or: in Security > Authentication Policies, make every Allow rule in every app sign-in policy require "Any 2 factor types".',
                    'Re-run the connector; one single-factor Allow rule fails the check.',
                ],
                doc_url: 'https://help.okta.com/oie/en-us/content/topics/identity-engine/policies/add-okta-sign-on-policy-rule.htm',
            },
            entra_posture: {
                steps: [
                    'In the Microsoft Entra admin center go to Entra ID > Conditional Access > Policies and select New policy.',
                    'Include All users and All resources; exclude only emergency-access accounts.',
                    'Under Grant select Require authentication strength (Multifactor authentication) or Require multifactor authentication, alone.',
                    'Set Enable policy to On (not Report-only) and save. Security defaults also satisfies this check.',
                ],
                doc_url: 'https://learn.microsoft.com/en-us/entra/identity/conditional-access/policy-all-users-mfa-strength',
            },
            microsoft_365: {
                steps: [
                    'In the Microsoft Entra admin center go to Entra ID > Conditional Access > Policies and select New policy.',
                    'Include All users and All resources; exclude only emergency-access accounts.',
                    'Under Grant require multifactor authentication or an authentication strength, set the policy On and save.',
                    'Without Entra ID P1, enable security defaults instead: Entra ID > Overview > Properties > Manage security defaults.',
                ],
                doc_url: 'https://learn.microsoft.com/en-us/entra/fundamentals/security-defaults',
            },
            google_workspace: {
                steps: [
                    'In the Google Admin console go to Security > Authentication > 2-step verification.',
                    'Select the top-level organizational unit, check Allow users to turn on 2-Step Verification, and set Enforcement to On.',
                    'Set a short new-user enrollment period and save.',
                    'The check passes only when every active (not suspended) user has 2SV enforced.',
                ],
                doc_url: 'https://support.google.com/a/answer/9176657?hl=en',
            },
        },
        version: 2,
        provenance: REVIEWED('Okta Help "Add a global session policy rule"; Microsoft Learn "Require MFA for all users with Conditional Access"; Google Workspace Admin Help "Deploy 2-Step Verification"; Snowflake Docs "Multi-factor authentication (MFA)"'),
    },
    {
        check_key: 'idp_phishing_resistant_auth',
        steps: [
            'Enable a phishing-resistant authenticator (FIDO2 security key, passkey, or platform authenticator).',
            'Require it in a sign-in policy that covers all users and all apps.',
            'Register break-glass accounts with hardware keys before enforcing.',
        ],
        doc_url: 'https://learn.microsoft.com/en-us/entra/identity/authentication/concept-authentication-strengths',
        byKind: {
            okta_posture: {
                steps: [
                    'In Security > Authenticators, make sure FIDO2 (WebAuthn) or Okta Verify with FastPass is active.',
                    'In Security > Authentication Policies, open each app sign-in policy.',
                    'In every Allow rule, set "Possession factor constraints are" to Phishing-resistant for every allowed authentication option.',
                    'Re-run the connector; any Allow rule without the constraint fails the check.',
                ],
                doc_url: 'https://help.okta.com/oie/en-us/content/topics/identity-engine/authenticators/pr-set-up-policies.htm',
            },
            entra_posture: {
                steps: [
                    'In Entra ID > Conditional Access > Policies, create or edit a policy that includes All users and All resources.',
                    'Under Grant select Require authentication strength and choose Phishing-resistant MFA strength.',
                    'Set Enable policy to On (not Report-only) and save.',
                ],
                doc_url: 'https://learn.microsoft.com/en-us/entra/identity/authentication/concept-authentication-strengths',
            },
            microsoft_365: {
                steps: [
                    'In Entra ID > Conditional Access > Policies, create or edit a policy that includes All users and All resources.',
                    'Under Grant select Require authentication strength and choose Phishing-resistant MFA strength, with no OR alternatives.',
                    'Set Enable policy to On (not Report-only) and save.',
                ],
                doc_url: 'https://learn.microsoft.com/en-us/entra/identity/authentication/concept-authentication-strengths',
            },
            google_workspace: {
                steps: [
                    'In the Google Admin console go to Security > Authentication > 2-step verification.',
                    'Select the organizational unit and under Methods choose Only security key.',
                    'Give users time to enroll security keys or passkeys before saving.',
                ],
                doc_url: 'https://support.google.com/a/answer/9176657?hl=en',
            },
        },
        version: 1,
        provenance: REVIEWED('Okta Help "Create phishing-resistant app sign-in policies"; Microsoft Learn "Overview of Conditional Access authentication strengths"; Google Workspace Admin Help "Deploy 2-Step Verification"'),
    },
    {
        check_key: 'idp_deprovisioning_timely',
        steps: [
            'List active accounts with no sign-in in the last 90 days, including accounts created over 90 days ago that never signed in.',
            'Deactivate or delete each account that belongs to someone who has left or no longer needs access.',
            'Automate the offboarding from your HR system so accounts are disabled on the leaving date.',
        ],
        doc_url: 'https://learn.microsoft.com/en-us/entra/identity/monitoring-health/howto-manage-inactive-user-accounts',
        byKind: {
            okta_posture: {
                steps: [
                    'In Directory > People, find active users whose last sign-in is over 90 days ago (or never, created over 90 days ago).',
                    'Deactivate departed users: select them and use More Actions > Deactivate.',
                    'In Workflow > Automations, add a User Inactivity in Okta automation that suspends or deactivates after 90 days.',
                ],
                doc_url: 'https://help.okta.com/en-us/content/topics/automation-hooks/automations-main.htm',
            },
            entra_posture: {
                steps: [
                    'In Entra ID > Users, add the Last interactive sign-in time column and filter to over 90 days ago.',
                    'Disable (Account enabled = No) or delete accounts that no longer need access, including never-used ones.',
                    'Automate with a Lifecycle Workflows inactive-user workflow or your HR-driven provisioning.',
                ],
                doc_url: 'https://learn.microsoft.com/en-us/entra/identity/monitoring-health/howto-manage-inactive-user-accounts',
            },
        },
        version: 1,
        provenance: REVIEWED('Okta Help "Automations"; Microsoft Learn "How to manage inactive user accounts"'),
    },
    {
        check_key: 'idp_admins_minimized',
        steps: [
            'List every account holding a top administrator role.',
            'Remove the role from anyone who does not need it daily; delegate narrower roles instead.',
            'Keep the count at or below the threshold (default 5), including break-glass accounts.',
        ],
        doc_url: 'https://learn.microsoft.com/en-us/entra/identity/role-based-access-control/best-practices',
        byKind: {
            okta_posture: {
                steps: [
                    'In the Okta Admin Console go to Security > Administrators > Admins.',
                    'For each user who does not need admin access, select Edit > Remove individual assignments (also check group assignments).',
                    'The check counts users with any admin role; keep it at or below the threshold (default 5).',
                ],
                doc_url: 'https://help.okta.com/oie/en-us/content/topics/security/admin-remove-assignment.htm',
            },
            entra_posture: {
                steps: [
                    'In Entra ID > Roles & admins, open Global Administrator and review active assignments.',
                    'Remove the role from users who do not need it and assign a least-privileged role instead.',
                    'Keep active Global Administrators at or below the threshold (default 5), including emergency-access accounts.',
                ],
                doc_url: 'https://learn.microsoft.com/en-us/entra/identity/role-based-access-control/best-practices',
            },
            microsoft_365: {
                steps: [
                    'In Entra ID > Roles & admins, open Global Administrator and review its members.',
                    'Remove the role from users who do not need it and assign a least-privileged role instead.',
                    'Keep enabled Global Administrator members at or below the threshold (default 5).',
                ],
                doc_url: 'https://learn.microsoft.com/en-us/entra/identity/role-based-access-control/best-practices',
            },
            google_workspace: {
                steps: [
                    'In the Google Admin console review which users hold the Super Admin role.',
                    'Move day-to-day admin work to delegated admin roles and remove Super Admin from those users.',
                    'Keep at least two but no more than the threshold (default 5) super admins.',
                ],
                doc_url: 'https://support.google.com/a/answer/9011373?hl=en',
            },
        },
        version: 2,
        provenance: REVIEWED('Okta Help "Remove an admin role assignment"; Microsoft Learn "Best practices for Microsoft Entra roles"; Google Workspace Admin Help "Security best practices for administrator accounts"; Snowflake Docs "Access control considerations"'),
    },
    {
        check_key: 'idp_privileged_access_jit',
        baseFor: ['entra_posture'],
        steps: [
            'In ID Governance > Privileged Identity Management > Microsoft Entra roles, open Roles > Global Administrator.',
            'Add assignments with Assignment type Eligible for the admins who need the role.',
            'Remove their permanent Active assignments; keep at most 2 active (break-glass) Global Administrators.',
            'Requires Microsoft Entra ID P2 or ID Governance licensing for PIM.',
        ],
        doc_url: 'https://learn.microsoft.com/en-us/entra/id-governance/privileged-identity-management/pim-how-to-add-role-to-user',
        version: 1,
        provenance: REVIEWED('Microsoft Learn "Assign Microsoft Entra roles in PIM"; "Manage emergency access admin accounts"'),
    },
    // ── SaaS data handling ────────────────────────────────────────────────
    {
        check_key: 'saas_external_sharing_restricted',
        steps: [
            'Turn off external file sharing by default, or limit it to allowlisted domains or existing guests.',
            'Apply the setting at the top level so no unit or site is more permissive.',
        ],
        doc_url: 'https://support.google.com/a/answer/60781?hl=en',
        byKind: {
            google_workspace: {
                steps: [
                    'In the Google Admin console go to Apps > Google Workspace > Drive and Docs > Sharing settings > Sharing options.',
                    'For the top-level unit and every organizational unit or group override, choose Off or Allowlisted Domains.',
                    'Save; any policy left at On fails the check.',
                ],
                doc_url: 'https://support.google.com/a/answer/60781?hl=en',
            },
            microsoft_365: {
                steps: [
                    'In the SharePoint admin center expand Policies and select Sharing.',
                    'Under External sharing set SharePoint to Existing guests or Only people in your organization.',
                    'Select Save. "Anyone" and "New and existing guests" fail the check.',
                ],
                doc_url: 'https://learn.microsoft.com/en-us/sharepoint/turn-external-sharing-on-or-off',
            },
        },
        version: 1,
        provenance: REVIEWED('Google Workspace Admin Help "Manage external sharing for your organization"; Microsoft Learn "Manage sharing settings for SharePoint and OneDrive"'),
    },
    {
        check_key: 'saas_audit_logging_enabled',
        steps: [
            'Confirm the tenant audit log is on and retained for your required period.',
            'Grant the connector read access to the audit log, then re-run it.',
        ],
        doc_url: 'https://learn.microsoft.com/en-us/purview/audit-log-enable-disable',
        byKind: {
            google_workspace: {
                steps: [
                    'Workspace audit logging is always on; this check fails only when the connector cannot read the Reports API.',
                    'In domain-wide delegation, grant the connector https://www.googleapis.com/auth/admin.reports.audit.readonly.',
                    'Impersonate an administrator with Reports privileges, then re-run the connector.',
                    'Log events are kept 6 months; export to BigQuery or a SIEM if you need longer retention.',
                ],
                doc_url: 'https://support.google.com/a/answer/7061566?hl=en',
            },
            microsoft_365: {
                steps: [
                    'Grant the connector AuditLog.Read.All so it can read Entra directory audits, then re-run it.',
                    'In the Microsoft Purview portal open Audit and, if shown, select Start recording user and admin activity.',
                    'Route Entra audit logs to a Log Analytics workspace or storage if you need retention beyond 30 days.',
                ],
                doc_url: 'https://learn.microsoft.com/en-us/purview/audit-log-enable-disable',
            },
            aws: {
                steps: [
                    'This is Pinecone audit-log delivery to your S3 bucket (the AWS connection lists the files; it never opens one).',
                    'In Pinecone, open Settings → Audit logs and check delivery is on, with the same bucket and folder as the AWS connection.',
                    'If the check says GAC Pro cannot list the bucket, allow the GAC Pro role s3:ListBucket on it in the bucket policy; then re-run the AWS connection.',
                ],
                doc_url: 'https://docs.pinecone.io/guides/production/configure-audit-logs',
            },
            openai: {
                steps: [
                    'This is ChatGPT Enterprise: GAC Pro lists the audit and authentication log files and never opens one.',
                    'In ChatGPT, create a compliance API key allowed only the audit and authentication log categories (a key that can read conversations is refused).',
                    'Add the workspace ID and that key to the OpenAI connection, then choose Collect now.',
                ],
                doc_url: 'https://developers.openai.com/cookbook/examples/chatgpt/compliance_api/logs_platform',
            },
        },
        version: 3,
        provenance: REVIEWED('Microsoft Learn "Turn auditing on or off"; Google Workspace Admin Help "Data retention and lag times"; Snowflake Docs "LOGIN_HISTORY view"; Pinecone "Configure audit logs"; OpenAI cookbook "Compliance Logs Platform quickstart"'),
    },
    {
        check_key: 'saas_audit_logs_current',
        steps: [
            'Open the ChatGPT compliance logs for the workspace and check new authentication logs arrive daily.',
            'If they stopped, check the compliance key is still active and allowed the authentication category, then choose Collect now.',
        ],
        doc_url: 'https://developers.openai.com/cookbook/examples/chatgpt/compliance_api/logs_platform',
        baseFor: ['openai'],
        version: 1,
        provenance: REVIEWED('OpenAI cookbook "Compliance Logs Platform quickstart"'),
    },
    {
        check_key: 'saas_audit_logs_retained',
        steps: [
            'Export ChatGPT compliance logs to your SIEM before OpenAI deletes them (30 days).',
            'Keep them for your required period: 90 days online for FedRAMP Moderate (AU-11), longer if your agencies require OMB M-21-31.',
            'On the OpenAI connection, name the Datadog index that keeps them, then choose Collect now on Datadog and on OpenAI.',
        ],
        doc_url: 'https://docs.datadoghq.com/logs/log_configuration/indexes/',
        baseFor: ['openai'],
        version: 1,
        provenance: REVIEWED('NIST SP 800-53 Rev 5 AU-11 (FedRAMP Moderate parameter); OMB M-21-31; Datadog "Indexes"'),
    },
    // ── Cloud ─────────────────────────────────────────────────────────────
    {
        check_key: 'cloud_audit_trail_centralized',
        steps: [
            'Send audit logs from every region and project to one central, access-controlled destination.',
            'Turn on integrity protection for the log files where the provider supports it.',
        ],
        doc_url: 'https://docs.aws.amazon.com/awscloudtrail/latest/userguide/cloudtrail-create-a-trail-using-the-console-first-time.html',
        byKind: {
            aws: {
                steps: [
                    'In the CloudTrail console create (or edit) a trail that applies to all regions.',
                    'Under Log file validation select Enabled, choose a central S3 bucket, and save.',
                    'Confirm the trail status shows Logging is on.',
                ],
                doc_url: 'https://docs.aws.amazon.com/awscloudtrail/latest/userguide/cloudtrail-create-a-trail-using-the-console-first-time.html',
                iac: { terraform: AWS_CLOUDTRAIL_TF, aws_config_rule: 'MULTI_REGION_CLOUD_TRAIL_ENABLED' },
            },
            gcp: {
                steps: [
                    'In Cloud Logging > Log Router, create a sink at the organization (or project) level.',
                    'For an organization sink, select Include logs ingested by this organization and all child resources.',
                    'Route to a central log bucket, BigQuery dataset or Cloud Storage bucket, and keep the sink enabled.',
                ],
                doc_url: 'https://docs.cloud.google.com/logging/docs/export/aggregated_sinks',
            },
        },
        version: 1,
        provenance: REVIEWED('AWS CloudTrail User Guide "Creating a trail with the CloudTrail console"; Google Cloud "Collate and route organization- and folder-level logs"'),
    },
    {
        check_key: 'cloud_config_compliant',
        steps: [
            'Open the list of non-compliant configuration rules or policies.',
            'Fix each resource (or the rule scope) until nothing reports non-compliant, then re-run the connector.',
        ],
        doc_url: 'https://docs.aws.amazon.com/config/latest/developerguide/evaluate-config_view-compliance.html',
        byKind: {
            aws: {
                steps: [
                    'In the AWS Config console open Rules and filter by Noncompliant in each audited region.',
                    'Open each rule, fix the listed resources, and select Re-evaluate.',
                    'Check Conformance packs too; any non-compliant rule in a tracked pack fails the check.',
                ],
                doc_url: 'https://docs.aws.amazon.com/config/latest/developerguide/evaluate-config_view-compliance.html',
            },
            gcp: {
                steps: [
                    'Approve the GCP mapping library in the GAC Pro review queue so the expected constraints are known.',
                    'In IAM & Admin > Organization policies, open each expected constraint the check lists as not enforced.',
                    'Select Manage policy, add a rule with Enforcement On, and save.',
                ],
                doc_url: 'https://docs.cloud.google.com/resource-manager/docs/organization-policy/creating-managing-policies',
            },
            azure_posture: {
                steps: [
                    'Open Policy > Compliance in the Azure portal (Azure Government: portal.azure.us) and filter Non-compliant.',
                    'Fix each resource the policy assignment flags, or edit the exclusion scope with a documented exception.',
                    'Trigger an on-demand evaluation scan so the next connector run picks up the change.',
                ],
                doc_url: 'https://learn.microsoft.com/azure/governance/policy/how-to/get-compliance-data',
            },
        },
        version: 1,
        provenance: REVIEWED('AWS Config Developer Guide "Viewing compliance information"; Google Cloud "Apply organization policies"; Microsoft Learn "Get policy compliance data"'),
    },
    {
        check_key: 'cloud_benchmarks_passing',
        steps: [
            'Enable the provider security benchmark service on the audited scope.',
            'Resolve every active HIGH or CRITICAL failed finding, or suppress it with a documented exception.',
        ],
        doc_url: 'https://docs.aws.amazon.com/securityhub/latest/userguide/enable-standards.html',
        byKind: {
            aws: {
                steps: [
                    'Enable Security Hub and at least one standard (e.g. AWS Foundational Security Best Practices).',
                    'In Findings, filter Compliance status FAILED, severity HIGH or CRITICAL, workflow NEW or NOTIFIED.',
                    'Remediate each finding, or set its workflow status to SUPPRESSED with a documented exception.',
                ],
                doc_url: 'https://docs.aws.amazon.com/securityhub/latest/userguide/enable-standards.html',
                iac: { terraform: AWS_SECURITYHUB_TF, aws_config_rule: 'SECURITYHUB_ENABLED' },
            },
            gcp: {
                steps: [
                    'Activate Security Command Center (Standard tier or above) on the organization or project.',
                    'In Findings, filter active MISCONFIGURATION findings with severity HIGH or CRITICAL.',
                    'Fix each resource, or mute the finding with a documented exception.',
                ],
                doc_url: 'https://docs.cloud.google.com/security-command-center/docs/activate-scc-overview',
            },
            azure_posture: {
                steps: [
                    'Enable Microsoft Defender for Cloud on every audited subscription (Settings > Environment settings).',
                    'In Defender for Cloud > Recommendations, filter Unhealthy and sort by Severity (High first).',
                    'Fix each resource or exempt the recommendation with a documented justification.',
                ],
                doc_url: 'https://learn.microsoft.com/azure/defender-for-cloud/implement-security-recommendations',
            },
        },
        version: 1,
        provenance: REVIEWED('AWS Security Hub User Guide "Enabling a security standard"; Google Cloud "Overview of activating Security Command Center"; Microsoft Learn "Remediate security recommendations"'),
    },
    {
        check_key: 'cloud_network_restricted',
        steps: [
            'Find firewall or security-group rules that allow SSH (22) or RDP (3389) from anywhere (0.0.0.0/0 or ::/0).',
            'Restrict the source to known ranges, or remove the rule and use a bastion or session manager instead.',
        ],
        doc_url: 'https://docs.aws.amazon.com/vpc/latest/userguide/security-group-rules.html',
        byKind: {
            aws: {
                steps: [
                    'In the EC2 console open Security Groups in each audited region.',
                    'Edit inbound rules that allow port 22 or 3389 (or all traffic) from 0.0.0.0/0 or ::/0.',
                    'Narrow the source CIDR or delete the rule; use Systems Manager Session Manager for admin access.',
                ],
                doc_url: 'https://docs.aws.amazon.com/vpc/latest/userguide/security-group-rules.html',
                iac: { aws_config_rule: 'INCOMING_SSH_DISABLED' },
            },
            gcp: {
                steps: [
                    'Enable Security Command Center so open-port findings are produced.',
                    'For each OPEN_SSH_PORT, OPEN_RDP_PORT or OPEN_FIREWALL finding, edit the VPC firewall rule.',
                    'Replace 0.0.0.0/0 with specific source ranges or use IAP TCP forwarding instead.',
                ],
                doc_url: 'https://docs.cloud.google.com/security-command-center/docs/concepts-security-health-analytics',
            },
        },
        version: 2,
        provenance: REVIEWED('Amazon VPC User Guide "Security group rules"; Google Cloud "Overview of Security Health Analytics"; Snowflake Docs "Controlling network traffic with network policies"'),
    },
    {
        check_key: 'cloud_asset_inventory_recording',
        steps: [
            'Turn on continuous resource-configuration recording in every audited region or scope.',
            'Grant the connector read access to the inventory, then re-run it.',
        ],
        doc_url: 'https://docs.aws.amazon.com/config/latest/developerguide/stop-start-recorder.html',
        byKind: {
            aws: {
                steps: [
                    'In the AWS Config console open Settings in each audited region.',
                    'Set up the configuration recorder to record all supported resource types and start recording.',
                    'Fix any delivery-channel error so the recorder status is not FAILURE.',
                ],
                doc_url: 'https://docs.aws.amazon.com/config/latest/developerguide/stop-start-recorder.html',
                iac: { terraform: AWS_CONFIG_RECORDER_TF },
            },
            gcp: {
                steps: [
                    'Cloud Asset Inventory records continuously; this check fails only if the connector cannot read it.',
                    'Grant the GAC Pro service account roles/cloudasset.viewer on the connector scope.',
                    'Enable the Cloud Asset API in the project, then re-run the connector.',
                ],
                doc_url: 'https://docs.cloud.google.com/asset-inventory/docs/overview',
            },
            azure_posture: {
                steps: [
                    'Azure Resource Graph records continuously; this check fails only if the service principal cannot read it.',
                    'Assign the Reader role to the GAC Pro app on every audited subscription (Access control (IAM) > Add role assignment).',
                    'Confirm the subscription is active (not disabled) and in the connected tenant, then re-run the connector.',
                ],
                doc_url: 'https://learn.microsoft.com/azure/governance/resource-graph/overview',
            },
        },
        version: 1,
        provenance: REVIEWED('AWS Config Developer Guide "Working with the configuration recorder"; Google Cloud "Cloud Asset Inventory overview"; Microsoft Learn "Azure Resource Graph overview"'),
    },
    {
        check_key: 'cloud_encryption_at_rest',
        baseFor: ['aws'],
        steps: [
            'In the EC2 console of each audited region select Settings, then the Data protection and security tab.',
            'Under EBS encryption choose Manage, enable encryption by default, and pick the default KMS key.',
            'Repeat for every audited region; existing unencrypted volumes need a snapshot copy to encrypt.',
        ],
        doc_url: 'https://docs.aws.amazon.com/ebs/latest/userguide/encryption-by-default.html',
        iac: { terraform: AWS_EBS_DEFAULT_TF, aws_config_rule: 'EC2_EBS_ENCRYPTION_BY_DEFAULT' },
        version: 1,
        provenance: REVIEWED('Amazon EBS User Guide "Enable Amazon EBS encryption by default"'),
    },
    {
        check_key: 'cloud_service_credentials_rotated',
        steps: [
            'Find long-lived access keys older than 90 days.',
            'Replace them with short-lived credentials (roles, workload identity) or rotate and delete the old key.',
        ],
        doc_url: 'https://docs.aws.amazon.com/IAM/latest/UserGuide/id-credentials-access-keys-update.html',
        byKind: {
            aws: {
                steps: [
                    'In IAM > Users, open each user with an active access key older than 90 days.',
                    'Create a new key, update the application, then deactivate and delete the old key.',
                    'Prefer IAM roles or IAM Identity Center so no long-lived user keys remain.',
                ],
                doc_url: 'https://docs.aws.amazon.com/IAM/latest/UserGuide/id-credentials-access-keys-update.html',
                iac: { aws_config_rule: 'ACCESS_KEYS_ROTATED' },
            },
            gcp: {
                steps: [
                    'In IAM & Admin > Service accounts, find user-managed keys older than 90 days.',
                    'Move workloads to attached service accounts or Workload Identity Federation, then delete the old keys.',
                    'Enforce constraints/iam.disableServiceAccountKeyCreation to stop new user-managed keys.',
                ],
                doc_url: 'https://docs.cloud.google.com/iam/docs/key-rotation',
                iac: { terraform: gcpOrgPolicyTf('iam.disableServiceAccountKeyCreation'), gcp_constraint: 'constraints/iam.disableServiceAccountKeyCreation' },
            },
        },
        version: 1,
        provenance: REVIEWED('AWS IAM User Guide "Update access keys"; Google Cloud "Service account key rotation"'),
    },
    {
        check_key: 'cloud_backups_configured',
        baseFor: ['aws'],
        steps: [
            'In the AWS Backup console create a backup plan with at least one rule (frequency and retention).',
            'Assign resources to the plan by tag or resource type.',
            'The check passes when at least one audited region has a backup plan.',
        ],
        doc_url: 'https://docs.aws.amazon.com/aws-backup/latest/devguide/creating-a-backup-plan.html',
        iac: { terraform: AWS_BACKUP_TF, aws_config_rule: 'BACKUP_PLAN_MIN_FREQUENCY_AND_MIN_RETENTION_CHECK' },
        version: 1,
        provenance: REVIEWED('AWS Backup Developer Guide "Create a backup plan"'),
    },
    {
        check_key: 'cloud_public_access_blocked',
        baseFor: ['gcp'],
        steps: [
            'Search IAM policies for allUsers and allAuthenticatedUsers on the connector scope.',
            'Remove those members from each binding unless the resource is intentionally public.',
            'Enforce constraints/storage.publicAccessPrevention, and restrict members with constraints/iam.allowedPolicyMemberDomains.',
        ],
        doc_url: 'https://docs.cloud.google.com/storage/docs/public-access-prevention',
        iac: { terraform: gcpOrgPolicyTf('storage.publicAccessPrevention'), gcp_constraint: 'constraints/storage.publicAccessPrevention' },
        version: 1,
        provenance: REVIEWED('Google Cloud Storage "Public access prevention"; Organization Policy constraints reference'),
    },
    {
        check_key: 'cloud_privileged_bindings_minimized',
        baseFor: ['gcp'],
        steps: [
            'In IAM on the connector scope, list principals holding Owner, Editor, Organization Administrator, Project IAM Admin or Security Admin.',
            'Replace broad roles with predefined least-privilege roles, preferably granted to groups.',
            'Keep between 1 and the threshold (default 4) privileged principals.',
        ],
        doc_url: 'https://docs.cloud.google.com/iam/docs/using-iam-securely',
        version: 1,
        provenance: REVIEWED('Google Cloud IAM "Use IAM securely"'),
    },
    // V109-3-3 (docs/v10.9/slice.md R07, R19): the two new slice check keys. Drafts until the owner reviews them.
    {
        check_key: 'cloud_data_access_audit_enabled',
        baseFor: ['gcp'],
        steps: [
            'In IAM & Admin > Audit Logs on the connector scope, select "All services" (allServices).',
            'Turn on Admin Read, Data Read and Data Write, then save.',
            'Data Access logs add volume and cost; set log exclusions for noisy services rather than turning the types off.',
        ],
        doc_url: 'https://cloud.google.com/logging/docs/audit/configure-data-access',
        version: 1,
        provenance: DRAFT('Google Cloud Logging "Enable Data Access audit logs"'),
    },
    {
        check_key: 'd1_data_location_restricted',
        baseFor: ['cloudflare'],
        steps: [
            'Set your organization\'s allowed data locations (SA-9(5)) on the GovRAMP program; until they are set the D1 rule says Couldn\'t check.',
            'Give the connector token D1 Read for this account, then collect again.',
            'Jurisdiction can\'t be changed: create a new D1 database with the required one (wrangler d1 create <name> --jurisdiction <eu|fedramp>).',
            'Migrate the data, switch the binding, then retire the old database.',
        ],
        doc_url: 'https://developers.cloudflare.com/d1/configuration/data-location/',
        version: 1,
        provenance: DRAFT('Cloudflare D1 "Data location"'),
    },
    // v10.9 LM-26 (R20): privileged roles granted just in time. Draft until the owner reviews it.
    {
        check_key: 'cloud_privileged_access_jit',
        baseFor: ['gcp'],
        steps: [
            'Grant the GAC Pro read-only service account roles/privilegedaccessmanager.viewer on the connector scope.',
            'In Privileged Access Manager, create entitlements for the Owner, Editor and admin roles people need, with a maximum duration of 8 hours or less.',
            'Remove the standing Owner, Editor and admin bindings from the scope; keep a documented break-glass account as an approved exception.',
        ],
        doc_url: 'https://cloud.google.com/iam/docs/pam-overview',
        version: 1,
        provenance: DRAFT('Google Cloud IAM "Privileged Access Manager overview"'),
    },
    {
        check_key: 'cloud_vuln_scanning_active',
        baseFor: ['aws'],
        steps: [
            'In the Amazon Inspector console activate Inspector in the audited account.',
            'Turn on both Amazon EC2 scanning and Amazon ECR scanning (Lambda is optional).',
        ],
        doc_url: 'https://docs.aws.amazon.com/inspector/latest/user/getting_started_tutorial.html',
        iac: { terraform: AWS_INSPECTOR_TF },
        version: 1,
        provenance: REVIEWED('Amazon Inspector User Guide "Getting started tutorial: Activating Amazon Inspector"'),
    },
    // ── AI provider account security ──────────────────────────────────────
    {
        check_key: 'ai_provider_admins_minimized',
        steps: [
            'List members with admin or owner roles in the AI provider organization.',
            'Downgrade anyone who does not manage members or keys to a developer or user role.',
        ],
        doc_url: 'https://support.anthropic.com/en/articles/10186004-api-console-roles-and-permissions',
        byKind: {
            anthropic: {
                steps: [
                    'In the Claude Console open organization settings > Members.',
                    'Change members who do not manage users from admin (or owner) to developer or user.',
                    'Keep admin, owner and primary owner at or below the threshold (default 5).',
                ],
                doc_url: 'https://support.anthropic.com/en/articles/10186004-api-console-roles-and-permissions',
            },
            openai: {
                steps: [
                    'In the OpenAI platform open organization settings > People.',
                    'Change users who do not administer the organization from Owner to Reader.',
                    'Keep Owners at or below the threshold (default 3).',
                ],
                doc_url: 'https://platform.openai.com/docs/guides/rbac',
            },
        },
        version: 1,
        provenance: REVIEWED('Claude Help Center "Claude Console roles and permissions"; OpenAI "Manage permissions in the OpenAI platform"'),
    },
    {
        check_key: 'ai_provider_keys_rotated',
        steps: [
            'Find API keys older than 90 days.',
            'Create a replacement, update the application, then disable or delete the old key.',
        ],
        doc_url: 'https://platform.claude.com/docs/en/manage-claude/admin-api',
        byKind: {
            anthropic: {
                steps: [
                    'In the Claude Console open API keys and sort by creation date.',
                    'For each active key older than 90 days, create a replacement in the same workspace and deploy it.',
                    'Set the old key to inactive or delete it.',
                ],
                doc_url: 'https://platform.claude.com/docs/en/manage-claude/admin-api',
            },
            openai: {
                steps: [
                    'Review project API keys and Admin API keys older than 90 days.',
                    'Create a replacement (set an expiration date where offered), deploy it, and revoke the old key.',
                    'Revoke Admin API keys that are no longer needed.',
                ],
                doc_url: 'https://help.openai.com/en/articles/5112595-best-practices-for-api-key-safety',
            },
            azure_openai: {
                steps: [
                    'Move every client to Microsoft Entra ID authentication (managed identity or service principal).',
                    'Set disableLocalAuth to true on each Azure OpenAI account so API keys stop working.',
                    'Assign the Azure Policy that requires key access disabled on Azure AI Services resources.',
                ],
                doc_url: 'https://learn.microsoft.com/en-us/azure/ai-services/disable-local-auth',
            },
        },
        version: 1,
        provenance: REVIEWED('Claude Platform Docs "Admin API"; OpenAI Help "Best practices for API key safety"; Microsoft Learn "Disable local authentication in Foundry Tools"'),
    },
    {
        check_key: 'ai_provider_audit_logging',
        steps: [
            'Turn on the provider organization audit log.',
            'Grant the connector read access to it, then re-run the connector.',
        ],
        doc_url: 'https://help.openai.com/en/articles/9687866-admin-and-audit-logs-api-for-the-api-platform',
        byKind: {
            anthropic: {
                steps: [
                    'Ask your Anthropic representative to enable the Compliance API for your organization.',
                    'Use an Admin key that carries the read:compliance_activities scope for the connector.',
                    'Re-run the connector; it passes once the activity feed returns events.',
                ],
                doc_url: 'https://platform.claude.com/docs/en/manage-claude/compliance-api',
            },
            openai: {
                steps: [
                    'As an organization owner open organization settings > Data controls > Data retention.',
                    'Under Audit logging select Enable and save (it cannot be turned off afterwards from settings).',
                    'Re-run the connector.',
                ],
                doc_url: 'https://help.openai.com/en/articles/9687866-admin-and-audit-logs-api-for-the-api-platform',
            },
            datadog: {
                steps: [
                    'In Organization Settings select Audit Trail Settings under Compliance and click Enable.',
                    'Give the connector application key the audit_logs_read scope.',
                    'Re-run the connector.',
                ],
                doc_url: 'https://docs.datadoghq.com/account_management/audit_trail/',
            },
        },
        version: 1,
        provenance: REVIEWED('OpenAI Help "Admin and Audit Logs API for the API Platform"; Claude Platform Docs "Compliance API"; Datadog Docs "Datadog Audit Trail"'),
    },
    // ── Vulnerability management ──────────────────────────────────────────
    {
        check_key: 'vuln_scanning_current',
        steps: [
            'Schedule vulnerability scans so the newest result is never older than 7 days.',
            'Fix scans that failed or stopped, then re-run the connector.',
        ],
        doc_url: 'https://docs.tenable.com/vulnerability-management/Content/Scans/BasicSettings.htm',
        byKind: {
            tenable: {
                steps: [
                    'In Tenable Vulnerability Management open each scan covering in-scope assets.',
                    'In Basic settings > Schedule, turn Enable Schedule on and set Frequency to Daily or Weekly.',
                    'Confirm the scan is not in the Trash folder and its last run completed.',
                ],
                doc_url: 'https://docs.tenable.com/vulnerability-management/Content/Scans/BasicSettings.htm',
            },
            vuln_scanner: {
                steps: [
                    'Run your scanner against in-scope assets at least weekly.',
                    'Upload each new scan export to GAC Pro (or push it from CI) within 7 days of the last one.',
                ],
                doc_url: 'https://docs.tenable.com/vulnerability-management/Content/Scans/ScanBestPractices.htm',
            },
        },
        version: 1,
        provenance: REVIEWED('Tenable Docs "Basic Settings in Vulnerability Management Scans"; "Scan Best Practices"'),
    },
    {
        check_key: 'vuln_remediation_within_timeframe',
        steps: [
            'List open vulnerabilities past the remediation deadline set by your program timeframes.',
            'Patch or mitigate each one, then rescan so it is reported fixed.',
            'Where a fix is not possible yet, record a risk acceptance in GAC Pro; accepted findings are not counted overdue.',
        ],
        doc_url: 'https://docs.tenable.com/vulnerability-management/Content/Act/RemediationGoals.htm',
        byKind: {
            tenable: {
                steps: [
                    'In Tenable Vulnerability Management filter open findings by severity and first-seen date.',
                    'Create a remediation goal (Within the number of days) matching your deadlines and assign owners.',
                    'Patch, then rescan so findings move to Fixed; record risk acceptances in GAC Pro where needed.',
                ],
                doc_url: 'https://docs.tenable.com/vulnerability-management/Content/Act/RemediationGoals.htm',
            },
            vuln_scanner: {
                steps: [
                    'Patch or mitigate overdue findings, then rescan.',
                    'Upload the new scan export to GAC Pro so fixed findings close.',
                    'Record risk acceptances in GAC Pro for findings you cannot fix before the deadline.',
                ],
                doc_url: 'https://docs.tenable.com/vulnerability-management/Content/Scans/ScanBestPractices.htm',
            },
            aws: {
                steps: [
                    'In Amazon Inspector open Findings and filter active findings by severity and age.',
                    'Patch the affected EC2 instances, images or Lambda functions; Inspector closes findings on rescan.',
                    'Record risk acceptances in GAC Pro for findings you cannot fix before the deadline.',
                ],
                doc_url: 'https://docs.aws.amazon.com/inspector/latest/user/getting_started_tutorial.html',
            },
        },
        version: 1,
        provenance: REVIEWED('Tenable Docs "Remediation Goals"; Amazon Inspector User Guide'),
    },
    // ── Source control ────────────────────────────────────────────────────
    {
        check_key: 'branch_protection_enabled',
        baseFor: ['github'],
        steps: [
            'In the repository go to Settings > Branches (or Settings > Rules > Rulesets).',
            'Add a branch protection rule or ruleset targeting the default branch.',
            'Block force pushes and deletions, and include administrators.',
        ],
        doc_url: 'https://docs.github.com/en/repositories/configuring-branches-and-merges-in-your-repository/managing-protected-branches/managing-a-branch-protection-rule',
        iac: { terraform: GITHUB_BRANCH_PROTECTION_TF },
        version: 1,
        provenance: REVIEWED('GitHub Docs "Managing a branch protection rule"'),
    },
    {
        check_key: 'required_reviews_gte_1',
        baseFor: ['github'],
        steps: [
            'Edit the default branch protection rule or ruleset.',
            'Enable Require a pull request before merging (Require pull request before merging in rulesets).',
            'Select Require approvals and set Required number of approvals before merging to 1 or more; save.',
        ],
        doc_url: 'https://docs.github.com/en/repositories/configuring-branches-and-merges-in-your-repository/managing-protected-branches/managing-a-branch-protection-rule',
        iac: { terraform: GITHUB_BRANCH_PROTECTION_TF },
        version: 1,
        provenance: REVIEWED('GitHub Docs "Managing a branch protection rule"; "Creating rulesets for a repository"'),
    },
    {
        check_key: 'secret_scanning_enabled',
        baseFor: ['github'],
        steps: [
            'In the repository go to Settings and, under Security and quality, click Advanced Security.',
            'Enable Secret Protection (secret scanning) and Push protection.',
            'For private repositories this requires GitHub Secret Protection (or Advanced Security) licensing.',
        ],
        doc_url: 'https://docs.github.com/en/code-security/secret-scanning/enabling-secret-scanning-features/enabling-secret-scanning-for-your-repository',
        iac: { terraform: GITHUB_SECRET_SCANNING_TF },
        version: 1,
        provenance: REVIEWED('GitHub Docs "Enabling secret scanning for your repository"'),
    },
    // ── AI governance checks (AI_PROVIDER_CHECKS) ─────────────────────────
    {
        check_key: 'ai_usage_logging_enabled',
        steps: [
            'Turn on request/usage logging for every AI endpoint, gateway or region in scope.',
            'Send the logs to a destination you control and restrict who can read them.',
        ],
        doc_url: 'https://docs.aws.amazon.com/bedrock/latest/userguide/model-invocation-logging.html',
        byKind: {
            aws: {
                steps: [
                    'In the Amazon Bedrock console of each audited region open Settings > Model invocation logging.',
                    'Turn logging on, select the Text data type, and choose CloudWatch Logs, S3, or both.',
                    'Repeat in every audited region; one region without text logging fails the check.',
                ],
                doc_url: 'https://docs.aws.amazon.com/bedrock/latest/userguide/model-invocation-logging.html',
                iac: { terraform: AWS_BEDROCK_LOGGING_TF },
            },
            gcp: {
                steps: [
                    'For each Vertex AI endpoint with a deployed model, enable request-response logging.',
                    'Set a sampling rate and a BigQuery destination table, by patching the endpoint or redeploying.',
                    'Re-run the connector; endpoints without logging are listed in the result.',
                ],
                doc_url: 'https://docs.cloud.google.com/vertex-ai/docs/predictions/online-prediction-logging',
            },
            azure_openai: {
                steps: [
                    'In the Azure portal open each Azure OpenAI resource > Monitoring > Diagnostic settings.',
                    'Add a diagnostic setting with the RequestResponse and Audit categories (or allLogs).',
                    'Send to a Log Analytics workspace, storage account or event hub and save.',
                ],
                doc_url: 'https://learn.microsoft.com/en-us/azure/ai-services/diagnostic-logging',
            },
            cloudflare_ai_gateway: {
                steps: [
                    'In the Cloudflare dashboard open AI > AI Gateway and select each gateway.',
                    'In Settings turn Logs on (collect logs).',
                    'Repeat for every gateway; any gateway with log collection off fails the check.',
                ],
                doc_url: 'https://developers.cloudflare.com/ai-gateway/observability/logging/',
            },
        },
        version: 1,
        provenance: REVIEWED('Amazon Bedrock User Guide "Monitor model invocation using CloudWatch Logs and Amazon S3"; Vertex AI "Use online inference logging"; Microsoft Learn "Enable diagnostic logging"; Cloudflare AI Gateway "Logging"'),
    },
    {
        check_key: 'siem_security_alerting',
        steps: [
            'In Datadog open Security > Cloud SIEM and confirm it is enabled for the organization.',
            'Under Detection Rules, enable the rules that match your log sources (start with the out-of-the-box rules).',
            'Route signals to people: add a Notification Rule under Security > Settings > Notification Rules, or add recipients to the rule cases.',
            'Choose Collect now on the Datadog connection; the check passes when at least one enabled rule notifies someone.',
        ],
        doc_url: 'https://docs.datadoghq.com/security/cloud_siem/',
        baseFor: ['datadog'],
        version: 1,
        provenance: REVIEWED('Datadog docs "Cloud SIEM", "Detection Rules", "Notification Rules"'),
    },
    // V102-9-6: SIEM content coverage / retention / logging-failure alerts (splunk, sentinel, datadog).
    {
        check_key: 'siem_coverage_identity_logs',
        steps: [
            'Confirm identity logs (Okta, Entra signins, Google Workspace, Windows Security, Linux auth) are flowing into the SIEM.',
            'Splunk: check index=main has those sourcetypes. Sentinel: SigninLogs/AuditLogs/SecurityAlert have recent rows. Datadog: identity sources show in 24h.',
            'Fix any missing forwarder, agent, Azure Diagnostic Setting, or Datadog log source, then re-run the check.',
        ],
        doc_url: 'https://learn.microsoft.com/azure/sentinel/data-connectors-reference',
        baseFor: ['splunk', 'sentinel', 'datadog'],
        version: 1,
        provenance: DRAFT('Splunk Docs "Monitor data sources"; Microsoft Learn "Microsoft Sentinel data connectors"; Datadog Docs "Log collection"'),
    },
    {
        check_key: 'siem_retention_ge_365d',
        steps: [
            'Set SIEM index / workspace retention to at least 365 days.',
            'Splunk: set frozenTimePeriodInSecs ≥ 31,536,000 on each index.',
            'Sentinel: on the Log Analytics workspace, set retentionInDays ≥ 365 (archive tier for longer).',
            'Datadog: raise each Logs Index > Retention to 365 or beyond.',
        ],
        doc_url: 'https://docs.splunk.com/Documentation/Splunk/latest/Indexer/Setaretirementandarchivingpolicy',
        baseFor: ['splunk', 'sentinel', 'datadog'],
        version: 1,
        provenance: DRAFT('Splunk Docs "Set a retirement and archiving policy"; Microsoft Learn "Log Analytics workspace retention"; Datadog Docs "Log indexes"'),
    },
    {
        check_key: 'siem_alerts_logging_failure',
        steps: [
            'Create a SIEM alert that fires when a required log source stops arriving.',
            'Splunk: add a scheduled saved search counting events per sourcetype in 15m, alerting on zero; set alert.track=1.',
            'Sentinel: enable Data Connector Health or an Analytics Rule on Heartbeat/Usage that triggers on ingestion gaps.',
            'Datadog: add a Cloud SIEM detection rule matching "logging failure" / "ingest" and notify on-call.',
        ],
        doc_url: 'https://learn.microsoft.com/azure/sentinel/monitor-data-connector-health',
        baseFor: ['splunk', 'sentinel', 'datadog'],
        version: 1,
        provenance: DRAFT('Splunk Docs "Alerts in Splunk"; Microsoft Learn "Monitor data connector health"; Datadog Docs "Cloud SIEM detection rules"'),
    },
    {
        check_key: 'change_tickets_have_approver',
        steps: [
            'Require a recorded approval on every change ticket before it is implemented.',
            'Jira: make the approver field required on the Change issue type (or add a workflow validator on the transition to Done).',
            'ServiceNow: use change approval policies so every change_request reaches the Approved state before implementation.',
        ],
        doc_url: 'https://support.atlassian.com/jira-cloud-administration/docs/specify-field-behavior/',
        baseFor: ['jira', 'servicenow'],
        version: 1,
        provenance: DRAFT('Atlassian Support "Specify field behavior"; ServiceNow Docs "Change approval policies"'),
    },
    {
        check_key: 'ai_log_retention_set',
        steps: [
            'Set an explicit retention period for AI logs that meets your policy.',
            'Export logs to long-term storage where the platform retention is shorter than required.',
        ],
        doc_url: 'https://docs.datadoghq.com/logs/log_configuration/indexes/',
        byKind: {
            cloudflare_ai_gateway: {
                steps: [
                    'In AI > AI Gateway open each gateway > Settings.',
                    'Set a log limit with Automatic Log Deletion, or enable Workers Logpush to your own storage.',
                    'Repeat for every gateway; a gateway with neither fails the check.',
                ],
                doc_url: 'https://developers.cloudflare.com/ai-gateway/observability/logging/legacy-logs/',
            },
            datadog: {
                steps: [
                    'On the Logs Configuration page, open the Indexes section and edit each index.',
                    'Set the retention period to at least 90 days (the check minimum).',
                    'Repeat for every index; any index under 90 days fails the check.',
                ],
                doc_url: 'https://docs.datadoghq.com/logs/log_configuration/indexes/',
            },
        },
        version: 1,
        provenance: REVIEWED('Datadog Docs "Indexes"; Cloudflare AI Gateway "Legacy Logs" and "Workers Logpush"'),
    },
    {
        check_key: 'ai_guardrails_configured',
        steps: [
            'Create a guardrail or content-filter policy that blocks harmful content on both prompts and responses.',
            'Attach it to every model deployment or application in scope.',
        ],
        doc_url: 'https://docs.aws.amazon.com/bedrock/latest/userguide/guardrails-create.html',
        byKind: {
            aws: {
                steps: [
                    'In the Amazon Bedrock console open Guardrails and select Create guardrail.',
                    'Configure content filters (e.g. Hate, Violence, Misconduct) with a strength above None.',
                    'Create the guardrail and apply it to your Bedrock invocations or agents.',
                ],
                doc_url: 'https://docs.aws.amazon.com/bedrock/latest/userguide/guardrails-create.html',
                iac: { terraform: AWS_BEDROCK_GUARDRAIL_TF },
            },
            gcp: {
                steps: [
                    'Enable the Model Armor API in each audited project.',
                    'In Security > Model Armor select Create template and configure its filters.',
                    'Call the template from your application or integrate it with Vertex AI.',
                ],
                doc_url: 'https://docs.cloud.google.com/model-armor/manage-templates',
            },
            azure_openai: {
                steps: [
                    'In the Foundry portal open Guardrails + controls > Content filters and create a content filter.',
                    'Enable blocking filters for both input (prompt) and output (completion).',
                    'Assign the content filter to every deployment; deployments without one fail the check.',
                ],
                doc_url: 'https://learn.microsoft.com/en-us/azure/foundry-classic/openai/how-to/content-filters',
            },
        },
        version: 1,
        provenance: REVIEWED('Amazon Bedrock User Guide "Create a guardrail"; Google Cloud "Model Armor: Create and manage templates"; Microsoft Learn "Configure content filters"'),
    },
    {
        check_key: 'ai_prompt_attack_filter',
        steps: [
            'Turn on prompt-injection / jailbreak detection in your guardrail or content filter.',
            'Set it to block (not detect or annotate only) on user input.',
        ],
        doc_url: 'https://docs.aws.amazon.com/bedrock/latest/userguide/guardrails-prompt-attack.html',
        byKind: {
            aws: {
                steps: [
                    'Edit a Bedrock guardrail and open Configure content filters.',
                    'Select Configure prompt attacks filter and set the input strength above None.',
                    'Save a new guardrail version and use it in your invocations.',
                ],
                doc_url: 'https://docs.aws.amazon.com/bedrock/latest/userguide/guardrails-prompt-attack.html',
                iac: { terraform: AWS_BEDROCK_GUARDRAIL_TF },
            },
            gcp: {
                steps: [
                    'Edit (or create) a Model Armor template.',
                    'Enable Prompt injection and jailbreak detection and choose a confidence level.',
                    'Save the template; at least one template must have the filter enabled.',
                ],
                doc_url: 'https://docs.cloud.google.com/model-armor/manage-templates',
            },
            azure_openai: {
                steps: [
                    'Edit the content filter assigned to each deployment.',
                    'Under input filters enable Prompt shields for jailbreak attacks with Annotate and block.',
                    'Save; "Annotate only" or an unassigned deployment fails the check.',
                ],
                doc_url: 'https://learn.microsoft.com/en-us/azure/ai-services/openai/concepts/content-filter-configurability',
            },
        },
        version: 1,
        provenance: REVIEWED('Amazon Bedrock User Guide "Detect prompt attacks with Amazon Bedrock Guardrails"; Google Cloud "Model Armor templates"; Microsoft Learn "Content filter configurability"'),
    },
    {
        check_key: 'ai_rate_limiting',
        baseFor: ['cloudflare_ai_gateway'],
        steps: [
            'In the Cloudflare dashboard open AI > AI Gateway and select each gateway.',
            'In Settings enable Rate-limiting and set a request limit, time period and technique (fixed or sliding).',
            'Repeat for every gateway; a gateway with no limit fails the check.',
        ],
        doc_url: 'https://developers.cloudflare.com/ai-gateway/features/rate-limiting/',
        version: 1,
        provenance: REVIEWED('Cloudflare AI Gateway "Rate limiting"'),
    },
    {
        check_key: 'ai_model_inventory',
        steps: [
            'Register every model you train, fine-tune or deploy in the platform model registry.',
            'Grant the connector read access to the registry, then re-run it.',
        ],
        doc_url: 'https://docs.aws.amazon.com/sagemaker/latest/dg/model-registry.html',
        byKind: {
            aws: {
                steps: [
                    'Create a SageMaker Model Registry model package group for each model you build or fine-tune.',
                    'Register model versions to it (Bedrock custom models also count).',
                    'Confirm the read-back role has AmazonSageMakerReadOnly and AmazonBedrockReadOnly.',
                ],
                doc_url: 'https://docs.aws.amazon.com/sagemaker/latest/dg/model-registry.html',
            },
            gcp: {
                steps: [
                    'Import or upload each model served from a Vertex AI endpoint into the Vertex AI Model Registry.',
                    'Deploy endpoints from the registry entry rather than ad hoc.',
                    'Confirm the GAC Pro service account has roles/aiplatform.viewer.',
                ],
                doc_url: 'https://docs.cloud.google.com/vertex-ai/docs/model-registry/introduction',
            },
            azure_openai: {
                steps: [
                    'This check fails only when deployments cannot be listed.',
                    'Grant the connector service principal Reader on the subscription holding the Azure OpenAI accounts.',
                    'Re-run the connector.',
                ],
                doc_url: 'https://learn.microsoft.com/en-us/azure/ai-foundry/openai/how-to/working-with-models',
            },
        },
        version: 1,
        provenance: REVIEWED('Amazon SageMaker "Model Registry"; Vertex AI "Introduction to Model Registry"; Microsoft Learn "Working with Azure OpenAI models"'),
    },
    {
        check_key: 'ai_model_cards',
        baseFor: ['aws'],
        steps: [
            'In the SageMaker AI console create a model card for each registered model.',
            'Fill in intended use, risk rating, training details and evaluation results.',
            'The check passes once at least one model card exists while registered models exist.',
        ],
        doc_url: 'https://docs.aws.amazon.com/sagemaker/latest/dg/model-cards.html',
        version: 1,
        provenance: REVIEWED('Amazon SageMaker "Amazon SageMaker Model Cards"'),
    },
    {
        check_key: 'ai_model_monitoring',
        steps: [
            'Set up a model-quality or drift monitor for every live inference endpoint.',
            'Route its alerts to an owner.',
        ],
        doc_url: 'https://docs.aws.amazon.com/sagemaker/latest/dg/model-monitor.html',
        byKind: {
            aws: {
                steps: [
                    'Enable data capture on each SageMaker endpoint.',
                    'Create a SageMaker Model Monitor monitoring schedule (data or model quality) for the endpoint.',
                    'The check passes when monitoring schedules exist for the deployed endpoints.',
                ],
                doc_url: 'https://docs.aws.amazon.com/sagemaker/latest/dg/model-monitor.html',
            },
            gcp: {
                steps: [
                    'For each Vertex AI endpoint with a deployed model, open its Model monitoring settings.',
                    'Turn on Enable model monitoring for this endpoint and set the window, thresholds and alert emails.',
                    'Every active endpoint needs a monitoring job.',
                ],
                doc_url: 'https://docs.cloud.google.com/vertex-ai/docs/model-monitoring/set-up-model-monitoring',
            },
        },
        version: 1,
        provenance: REVIEWED('Amazon SageMaker "Data and model quality monitoring with Model Monitor"; Vertex AI "Set up model monitoring"'),
    },
    {
        check_key: 'ai_api_keys_managed',
        steps: [
            'Give every AI API key a named owner and scope it to a project or workspace.',
            'Rotate keys older than 90 days and revoke keys unused for 90 days.',
        ],
        doc_url: 'https://help.openai.com/en/articles/5112595-best-practices-for-api-key-safety',
        byKind: {
            anthropic: {
                steps: [
                    'In the Claude Console create named workspaces and issue keys inside them, not the default workspace.',
                    'Replace keys with no workspace or no recorded creator, and rotate keys older than 90 days.',
                    'Set replaced keys to inactive.',
                ],
                doc_url: 'https://platform.claude.com/docs/en/manage-claude/workspaces',
            },
            openai: {
                steps: [
                    'Issue project keys owned by a user or a project service account, never shared personal keys.',
                    'Rotate keys older than 90 days and revoke keys not used in 90 days.',
                    'Set an expiration date on new project keys where offered.',
                ],
                doc_url: 'https://help.openai.com/en/articles/5112595-best-practices-for-api-key-safety',
            },
            pinecone: {
                steps: [
                    'Find the keys the check lists as full access (ProjectEditor) in each Pinecone project.',
                    'Create replacement keys with only the roles each service needs (for example DataPlaneViewer for queries, DataPlaneEditor for ingest).',
                    'Move each service to its scoped key, then delete the full-access key and re-run the connection.',
                ],
                doc_url: 'https://docs.pinecone.io/guides/projects/manage-api-keys',
            },
        },
        version: 1,
        provenance: REVIEWED('OpenAI Help "Best practices for API key safety"; Claude Platform Docs "Workspaces"; Pinecone "Manage API keys"'),
    },
    {
        check_key: 'ai_rag_sources_governed',
        baseFor: ['aws'],
        steps: [
            'In the Amazon Bedrock console open Knowledge Bases.',
            'For each knowledge base with no data source, connect its governed source (e.g. an S3 bucket) and sync it.',
            'Delete knowledge bases that are no longer used.',
        ],
        doc_url: 'https://docs.aws.amazon.com/bedrock/latest/userguide/data-source-connectors.html',
        version: 1,
        provenance: REVIEWED('Amazon Bedrock User Guide "Connect a data source to your knowledge base"'),
    },
    {
        check_key: 'vector_store_encrypted',
        steps: [
            'Confirm the vector store encrypts data at rest.',
            'Use customer-managed keys (CMEK) where your policy requires key control.',
        ],
        doc_url: 'https://docs.pinecone.io/guides/operations/understanding-security',
        byKind: {
            gcp: {
                steps: [
                    'Vertex AI Vector Search encrypts indexes by default; this check fails only if indexes cannot be read.',
                    'Grant the GAC Pro service account roles/aiplatform.viewer and re-run the connector.',
                    'For CMEK, create the index and index endpoint with the same Cloud KMS key in the same region.',
                ],
                doc_url: 'https://docs.cloud.google.com/vertex-ai/docs/general/cmek',
            },
            pinecone: {
                steps: [
                    'Pinecone encrypts data at rest with AES-256; this check fails only if indexes cannot be listed.',
                    'Give the connector an API key with read access to the project indexes and re-run it.',
                    'For key control, create a CMEK-enabled project with an AWS KMS key.',
                ],
                doc_url: 'https://docs.pinecone.io/guides/production/configure-cmek',
            },
        },
        version: 1,
        provenance: REVIEWED('Pinecone Docs "Security overview" and "Configure customer-managed encryption keys"; Vertex AI "Customer-managed encryption keys (CMEK)"'),
    },
    {
        check_key: 'agent_memory_retention',
        baseFor: ['aws'],
        steps: [
            'List the Bedrock AgentCore memories in each audited region.',
            'Set the event expiry (eventExpiryDuration) on each memory to 365 days or less.',
            'Expiry applies to events written after the change; plan for older events to age out.',
        ],
        doc_url: 'https://docs.aws.amazon.com/bedrock-agentcore/latest/devguide/memory-create-a-memory-store.html',
        version: 1,
        provenance: REVIEWED('Amazon Bedrock AgentCore Developer Guide "Create an AgentCore Memory"; API reference "UpdateMemory"'),
    },
    {
        check_key: 'ai_service_authn_required',
        steps: [
            'Turn on authentication for the service so an anonymous request is refused.',
            'Store the key in a Kubernetes Secret and give the in-network collector a read-only key through its values file.',
            'Re-run the collector; a service that answers an anonymous request fails the check.',
        ],
        doc_url: 'https://qdrant.tech/documentation/guides/security/',
        byKind: {
            qdrant: {
                steps: [
                    'Set a read-only and an admin API key: service.read_only_api_key and service.api_key in the config, or QDRANT__SERVICE__READ_ONLY_API_KEY / QDRANT__SERVICE__API_KEY.',
                    'Enable TLS so the key is not sent in clear text.',
                    'Give the collector the read-only key (targets[].secretEnv) and re-run it.',
                ],
                doc_url: 'https://qdrant.tech/documentation/guides/security/',
            },
            weaviate: {
                steps: [
                    'Turn off anonymous access: AUTHENTICATION_ANONYMOUS_ACCESS_ENABLED=false.',
                    'Enable API-key auth (AUTHENTICATION_APIKEY_ENABLED=true) with a read-only key for the collector, or OIDC.',
                    'Give the collector its key and re-run it.',
                ],
                doc_url: 'https://weaviate.io/developers/weaviate/configuration/authentication',
            },
            vllm: {
                steps: [
                    'Start the OpenAI-compatible server with --api-key (or VLLM_API_KEY) so requests without the key are refused.',
                    'Give the collector the key and re-run it.',
                ],
                doc_url: 'https://docs.vllm.ai/en/latest/serving/openai_compatible_server.html',
            },
            ollama: {
                steps: [
                    'Ollama has no authentication of its own: do not expose port 11434 beyond the pods that need it (a NetworkPolicy).',
                    'Put an authenticating reverse proxy in front of it and point the collector at the proxy.',
                    'Re-run the collector; the check passes when the proxy refuses an anonymous call.',
                ],
                doc_url: 'https://github.com/ollama/ollama/blob/main/docs/faq.md',
            },
            aws: {
                steps: [
                    'Find the AgentCore gateways whose inbound authorization is NONE (named in the check details).',
                    'Update each one to use IAM (AWS_IAM) or a JWT authorizer (CUSTOM_JWT) from your identity provider.',
                    'Re-run the AWS connection; the check passes when no gateway accepts anonymous calls.',
                ],
                doc_url: 'https://docs.aws.amazon.com/bedrock-agentcore/latest/devguide/gateway-inbound-auth.html',
            },
        },
        version: 1,
        provenance: REVIEWED('Qdrant "Security"; Weaviate "Authentication"; vLLM "OpenAI-compatible server" (--api-key); Ollama FAQ; Amazon Bedrock AgentCore Developer Guide "Set up inbound authorization for your gateway"'),
    },
    {
        check_key: 'ai_threat_detection_active',
        steps: [
            'In Security Command Center, turn on Event Threat Detection for each project the check names (and Agent Engine Threat Detection when you run Vertex AI agents).',
            'Open the AI security dashboard and triage the open high or critical AI findings: fix the cause, or mute a finding you accept with a reason.',
            'Re-run the GCP connection; the check passes when detection is on and no high or critical AI finding is open.',
        ],
        doc_url: 'https://docs.cloud.google.com/security-command-center/docs/ai-protection-overview',
        baseFor: ['gcp'],
        version: 1,
        provenance: REVIEWED('Google Cloud "AI Protection overview"; "Review AI security"; "AI threat findings"'),
    },
    // V103-8-3: edge posture (Cloudflare account collector). Drafted 2026-10-07, pending owner review.
    {
        check_key: 'edge_dnssec_enabled',
        baseFor: ['cloudflare'],
        steps: [
            'In the Cloudflare dashboard open each zone the check names and go to DNS > Settings.',
            'Select Enable DNSSEC and add the DS record Cloudflare shows at your domain registrar.',
            'The check passes once every active zone reports DNSSEC as active (pending zones still fail).',
        ],
        doc_url: 'https://developers.cloudflare.com/dns/dnssec/',
        version: 1,
        provenance: DRAFT('Cloudflare DNS "DNSSEC"'),
    },
    {
        check_key: 'edge_tls_enforced',
        baseFor: ['cloudflare'],
        steps: [
            'In each zone the check names open SSL/TLS > Edge Certificates.',
            'Set Minimum TLS Version to TLS 1.2 or higher.',
            'Turn on Always Use HTTPS so plain HTTP requests are redirected.',
        ],
        doc_url: 'https://developers.cloudflare.com/ssl/edge-certificates/additional-options/minimum-tls/',
        version: 1,
        provenance: DRAFT('Cloudflare SSL/TLS "Minimum TLS Version"; "Always Use HTTPS"'),
    },
    {
        check_key: 'edge_hsts_enabled',
        baseFor: ['cloudflare'],
        steps: [
            'Confirm the zone serves every subdomain over HTTPS before turning HSTS on; browsers cache it for the max age.',
            'In SSL/TLS > Edge Certificates enable HTTP Strict Transport Security (HSTS) with a max age of at least 6 months.',
            'Include subdomains when every subdomain is HTTPS-only.',
        ],
        doc_url: 'https://developers.cloudflare.com/ssl/edge-certificates/additional-options/http-strict-transport-security/',
        version: 1,
        provenance: DRAFT('Cloudflare SSL/TLS "HTTP Strict Transport Security (HSTS)"'),
    },
    {
        check_key: 'edge_waf_managed_rules',
        baseFor: ['cloudflare'],
        steps: [
            'In each production zone the check names open Security > WAF > Managed rules.',
            'Deploy the Cloudflare Managed Ruleset (and the OWASP Core Ruleset where your plan includes it).',
            'List your production zones on the connection so the check covers only them, then re-run it.',
        ],
        doc_url: 'https://developers.cloudflare.com/waf/managed-rules/deploy-zone-dashboard/',
        version: 1,
        provenance: DRAFT('Cloudflare WAF "Deploy a managed ruleset"'),
    },
    {
        check_key: 'edge_admin_access_protected',
        baseFor: ['cloudflare'],
        steps: [
            'In Cloudflare One open Access > Applications and add a self-hosted application for each admin hostname the check names.',
            'Attach an Allow policy that requires your identity provider (and MFA) for the admin group only.',
            'Re-run the connector; a hostname is covered when an Access application domain matches it exactly or by wildcard.',
        ],
        doc_url: 'https://developers.cloudflare.com/cloudflare-one/applications/configure-apps/self-hosted-public-app/',
        version: 1,
        provenance: DRAFT('Cloudflare One "Publish a self-hosted application to the Internet"'),
    },
];
/** Base hints with each manifest's `outputs.hints` merged into `byKind` (G-1i). */
export const REMEDIATION_HINTS = BASE_HINTS.map((h) => {
    const extra = Object.fromEntries(CONNECTOR_MANIFESTS.flatMap((m) => (m.outputs.hints?.[h.check_key] ? [[m.kind, m.outputs.hints[h.check_key]]] : [])));
    return Object.keys(extra).length ? { ...h, byKind: { ...h.byKind, ...extra } } : h;
});
const BY_KEY = new Map(REMEDIATION_HINTS.map((h) => [h.check_key, h]));
export function remediationHint(checkKey) {
    return BY_KEY.get(checkKey);
}
/** The body to use for a connector kind: its variant, else the base hint. */
export function remediationHintFor(checkKey, connectorKind) {
    const h = BY_KEY.get(checkKey);
    if (!h)
        return undefined;
    const body = (connectorKind && h.byKind?.[connectorKind]) || h;
    const reviewed = h.provenance.reviewer !== null && !(body !== h && body.draft);
    return { steps: body.steps, doc_url: body.doc_url, iac: body.iac, version: h.version, reviewed, source: h.provenance.source };
}
//# sourceMappingURL=remediation-hints.js.map